import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAllContent } from "@/hooks/useContent";
import { CONSENT_EVENT, effectiveConsent } from "@/lib/consent";
import { isFramed } from "@/lib/framed";
import { isLandingPath } from "@/lib/landingPaths";
import { containersForPath, injectGtm, parseGtmRules, DEFAULT_GTM_ID } from "@/lib/gtm";


declare global {
  interface Window {
    clarity?: ((...args: unknown[]) => void) & { q?: unknown[] };
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    __clarityLoadedId?: string;
    __gaLoadedId?: string;
    __contentsquareLoadedId?: string;
    _gaLoaded?: boolean;
  }
}

const truthy = (v?: string) => {
  if (v === undefined || v === null) return true; // default ON when key absent
  const t = String(v).trim().toLowerCase();
  return t === "" || t === "true" || t === "1" || t === "yes" || t === "on";
};

const getStr = (
  content: Record<string, { value: string; updated_at: string }> | undefined,
  key: string,
  fallback: string,
) => {
  const v = content?.[key]?.value;
  return v && v.trim().length > 0 ? v.trim() : fallback;
};

function injectClarity(id: string) {
  if (window.__clarityLoadedId === id) return;
  // If a different ID was previously loaded, we cannot easily tear down — but
  // setting the new ID via the queue is enough for new events.
  window.__clarityLoadedId = id;

  // Standard Clarity bootstrap (matches the snippet provided by Microsoft).
  /* eslint-disable @typescript-eslint/no-explicit-any */
  (function (c: any, l: Document, a: string, r: string, i: string) {
    c[a] =
      c[a] ||
      function () {
        (c[a].q = c[a].q || []).push(arguments);
      };
    const t = l.createElement(r) as HTMLScriptElement;
    t.async = true;
    t.src = "https://www.clarity.ms/tag/" + i;
    const y = l.getElementsByTagName(r)[0];
    y?.parentNode?.insertBefore(t, y);
  })(window, document, "clarity", "script", id);
  /* eslint-enable @typescript-eslint/no-explicit-any */
}

function injectGA(id: string) {
  if (window.__gaLoadedId === id) return;
  window.__gaLoadedId = id;

  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params -- gtag.js requires Arguments, not a plain array
  const gtag = function () {
    window.dataLayer?.push(arguments);
  };
  window.gtag = window.gtag || (gtag as typeof window.gtag);

  // Only load the script tag once per ID
  if (!document.querySelector(`script[data-ga-id="${id}"]`)) {
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
    s.setAttribute("data-ga-id", id);
    document.head.appendChild(s);
  }

  window.gtag!("js", new Date());
  window.gtag!("config", id);
}

function injectContentsquare(id: string) {
  if (window.__contentsquareLoadedId === id) return;
  window.__contentsquareLoadedId = id;

  if (!document.querySelector(`script[data-contentsquare-id="${id}"]`)) {
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://t.contentsquare.net/uxa/${id}.js`;
    s.setAttribute("data-contentsquare-id", id);
    document.head.appendChild(s);
  }
}

const AnalyticsLoader = () => {
  const location = useLocation();
  const { data: content, isFetched } = useAllContent();
  const contentReady = isFetched || content !== undefined;
  const isAdmin = location.pathname.startsWith("/112076");
  const onLanding = isLandingPath(location.pathname);
  const [consentTick, setConsentTick] = useState(0);

  useEffect(() => {
    const handler = () => setConsentTick((n) => n + 1);
    window.addEventListener(CONSENT_EVENT, handler);
    return () => window.removeEventListener(CONSENT_EVENT, handler);
  }, []);

  // Google Tag Manager (owner decision): loads for every visitor, per-page rules from admin.
  // Wait for admin settings: a loaded container cannot be unloaded.
  useEffect(() => {
    if (isAdmin || isFramed() || !contentReady) return;
    const ids = containersForPath(location.pathname, {
      enabled: truthy(content?.["analytics.gtm_enabled"]?.value),
      id: getStr(content, "analytics.gtm_id", DEFAULT_GTM_ID),
      rules: parseGtmRules(content?.["analytics.gtm_rules"]?.value ?? ""),
    });
    ids.forEach(injectGtm);
  }, [isAdmin, location.pathname, content, contentReady]);

  useEffect(() => {
    // No third-party scripts in admin or inside frames (admin previews).
    if (isAdmin || isFramed()) return;

    // Landing pages (owner decision): tracking always loads, no banner is shown there.
    const consent = onLanding ? { analytics: true, replay: true } : effectiveConsent();
    // Elsewhere, only load third-party tracking after the user has granted consent.
    if (!consent.analytics && !consent.replay) return;

    const clarityEnabled = truthy(content?.["analytics.clarity_enabled"]?.value);
    const clarityId = getStr(content, "analytics.clarity_id", "whaiu1hmzn");

    const gaEnabled = truthy(content?.["analytics.ga_enabled"]?.value);
    const gaId = getStr(content, "analytics.ga_id", "G-LJGRMTZWYL");

    const csEnabled = truthy(content?.["analytics.contentsquare_enabled"]?.value);
    const csId = getStr(content, "analytics.contentsquare_id", "13697002b0b11");

    const run = () => {
      if (consent.analytics && clarityEnabled && clarityId) injectClarity(clarityId);
      if (consent.analytics && gaEnabled && gaId) injectGA(gaId);
      if (consent.replay && csEnabled && csId) injectContentsquare(csId);
    };

    if ("requestIdleCallback" in window) {
      (window as unknown as {
        requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => void;
      }).requestIdleCallback(run, { timeout: 3000 });
    } else {
      setTimeout(run, 1500);
    }
  }, [isAdmin, content, consentTick]);

  return null;
};

export default AnalyticsLoader;
